import React, { useEffect, useRef } from "react";
import { useEdit } from "../context/EditContext";
import { useMetadata } from "../context/MetadataContext";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import {
  SliderField,
  ColorField,
  ChoiceField,
  TextField,
  SwitchField,
  CheckboxField,
  IconPickerField,
  BorderStyleField,
  RoundedBorderField,
  PositionField,
  TextPositionField,
  SpacingField,
  ByLayoutSpacingField,
  ByLayoutTextPositionField,
  ByLayoutChoiceField,
  NestedField,
  LayoutByLayoutField,
  MultipleColorsField,
  PatternField,
  ImageField,
  FileField,
  DirectionsField,
  FontFamilyField,
  FontSizeField,
  WebsitePageField,
  FontWeightField,
  InteractiveCropField,
  BooleanChoiceField,
  ButtonsPerRowField,
  ByLayoutIntegerField,
  ByLayoutFontSizeField,
  MobileMenuIconVariantField,
  TextDecorationField,
  TextTransformField,
  TextTransformAnimationField,
  TextOffsetCopyField,
  ByLayoutSliderField,
  ImageShapeField,
  ImageRotationField,
  HeroLayoutField,
  HeroColumnWidthsField,
  HeroModeField,
  ComponentModeField,
  HeroSlotManagerField,
  ComponentSlotManagerField,
  EntranceAnimationField,
  ButtonFixedWidthField,
  VideoUrlField,
  BlurField,
  ContainerEffectField,
  LetterSpacingField,
  LineHeightField,
} from "./fields";

const toPaletteStorageSegment = (value, fallback) => {
  if (typeof value !== "string") return fallback;

  const trimmedValue = value.trim().toLowerCase();
  if (!trimmedValue) return fallback;

  return trimmedValue.replace(/\s+/g, "-");
};

const getPaletteStorageScope = (fieldName, metadata) => {
  const group = metadata?.group;
  const rawGroupLabel = typeof group === "string"
    ? group
    : group?.name || group?.title || group?.description;

  const groupSegment = toPaletteStorageSegment(rawGroupLabel, "section");
  const fieldSegment = toPaletteStorageSegment(fieldName, "field");

  return `multiple-colors:${groupSegment}:${fieldSegment}`;
};

const EditField = ({ field, value, metadata, onChange, allValues, parentFieldPath, sharesRow = false }) => {
  const { websiteId, selectedElement, selectElement, saveStatus, updateMultipleFields, lockedFields } = useEdit();
  const { componentModes } = useMetadata();
  const pendingRefreshRef = useRef(false);
  useEffect(() => {
    if (pendingRefreshRef.current && saveStatus === "saved" && selectedElement) {
      pendingRefreshRef.current = false;
      selectElement(selectedElement);
    }
  }, [saveStatus, selectedElement, selectElement]);

  // Hero mode field suppression: skip rendering locked fields
  if (lockedFields) {
    const fullPath = parentFieldPath ? `${parentFieldPath}.${field}` : field;
    if (lockedFields.has(field) || lockedFields.has(fullPath)) {
      return null;
    }
  }
  
  // Conditional rendering for color transform fields - only show when 'color-shift' is selected
  if (field === 'text_hover_transform_color') {
    const hoverTransforms = allValues.text_hover_transform || [];
    if (!Array.isArray(hoverTransforms) || !hoverTransforms.includes('color-shift')) {
      return null;  // Don't render the color field if color-shift is not selected
    }
  }
  
  if (field === 'text_click_transform_color') {
    const clickTransforms = allValues.text_click_transform || [];
    if (!Array.isArray(clickTransforms) || !clickTransforms.includes('color-shift')) {
      return null;  // Don't render the color field if color-shift is not selected
    }
  }

  const normalizedMetadata = metadata;
  
  const commonProps = {
    label: normalizedMetadata.appearance_title || field,
    help: normalizedMetadata.help_text,
    value: value,
    onChange: (val) => {
      onChange(field, val);
    },
  };

  // Helper to wrap any control with an info icon shown on hover (uses appearance_info_text)
  // Note: We will skip this wrapper for specific fields (e.g., switch) that render the icon next to the label directly
  const withInfo = (node) => {
    if (!normalizedMetadata || !normalizedMetadata.appearance_info_text) return node;
    return (
      <div className="relative">
        <div className="group absolute top-0 right-0">
          <span
            className={`inline-flex items-center justify-center w-4 h-4 rounded-full border ${borders.strong} ${textColors.muted} leading-none ${surfaces.elevated} cursor-help select-none ${typography.caption}`}
            aria-label="More info"
          >
            i
          </span>
          <div
            role="tooltip"
            className={`hidden group-hover:block absolute right-5 top-0 z-50 px-2 py-1 rounded bg-gray-800 dark:bg-dark-900 !text-white shadow max-w-xs ${typography.caption}`}
            style={{ whiteSpace: 'normal' }}
          >
            {normalizedMetadata.appearance_info_text}
          </div>
        </div>
        {node}
      </div>
    );
  };

  // Only short-circuit to NestedField when the appearance itself is plain 'nested'.
  // Some special controls are delivered as nested types in metadata but declare a
  // special appearance (e.g., 'special_hero_column_widths'); those must fall through
  // to the switch below.
  if (
    normalizedMetadata.appearance === "nested" && normalizedMetadata.nested_fields
  ) {
    return withInfo(
      <NestedField
        field={field}
        value={value}
        metadata={normalizedMetadata}
        onChange={onChange}
        parentFieldPath={parentFieldPath}
      />
    );
}

  switch (normalizedMetadata.appearance) {
    case "slider":
      // Prefer backend-provided suggestions (appearance_suggestions) and fall back to local presets.
      let suggestions = Array.isArray(metadata?.appearance_suggestions)
        ? metadata.appearance_suggestions
        : undefined;
      if (!suggestions && field === "icon_size") {
        suggestions = [10, 15, 20, 30];
      }
      const unlimitedLabel = field === "max_width" ? "Unlimited" : undefined;
      return (
        <SliderField
          {...commonProps}
          min={metadata.appearance_min}
          max={metadata.appearance_max}
          step={metadata.appearance_step_size}
          infoText={metadata.appearance_info_text}
          suggestions={suggestions}
          unlimitedLabel={unlimitedLabel}
        />
      );

    case "color":
      return (
        <ColorField
          {...commonProps}
          field={field}
          nullable={field === "background_color" || field === "color_tint"}
          nullLabel={field === "color_tint" ? "No Tint" : undefined}
          infoText={metadata.appearance_info_text}
        />
      );
    case "special_left_sidebar_toggle_color":
    case "special_left_sidebar_toggle_hover_color":
    case "special_right_sidebar_toggle_color":
    case "special_right_sidebar_toggle_hover_color":
      return (
        <ColorField
          {...commonProps}
          field={field}
          infoText={metadata.appearance_info_text}
        />
      );

    case "choices":
      return (
        <ChoiceField
          {...commonProps}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );
    case "special_image_shape":
      return (
        <ImageShapeField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            // no preview refresh required; purely CSS based in renderer
          }}
          choices={metadata.choices?.map((choice) => ({ value: choice.value, label: choice.label }))}
          infoText={metadata.appearance_info_text}
        />
      );
    
    case "special_image_rotation":
      return (
        <ImageRotationField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          infoText={metadata.appearance_info_text}
        />
      );
    
    case "special_border_style":
      return (
        <BorderStyleField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          allValues={allValues}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_rounded_border":
      return (
        <RoundedBorderField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_blur":
      return (
        <BlurField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            // Blur might need refresh if it affects layout? 
            // Usually not, but if it's on an image it's just CSS class.
            // But if we want safe side, we can set pendingRefresh.
            // RoundedBorderField doesn't set it (EditField line 215).
            // So I won't set it unless needed.
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_letter_spacing":
      return (
        <LetterSpacingField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_line_height":
      return (
        <LineHeightField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_container_effect":
      return (
        <ContainerEffectField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_button_fixed_width":
      return (
        <ButtonFixedWidthField
          {...commonProps}
          onChange={(partial) => onChange(field, { ...(value || {}), ...partial })}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_mobile_menu_icon_variant":
      return (
        <MobileMenuIconVariantField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_font_family":
      return (
        <FontFamilyField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            // Mark to refresh after autosave confirms
            pendingRefreshRef.current = true;
          }}
          allValues={allValues}
          selectedElement={selectedElement}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_font_size":
      return (
        <FontSizeField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          allValues={allValues}
          selectedElement={selectedElement}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_font_weight":
      return (
        <FontWeightField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          allValues={allValues}
          selectedElement={selectedElement}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_text_decoration":
      return (
        <TextDecorationField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_text_transform":
      return (
        <TextTransformField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          allValues={allValues}
          selectedElement={selectedElement}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_text_offset_copy":
      return (
        <TextOffsetCopyField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            // Pure CSS effect in renderer; no refetch required.
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          allValues={allValues}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_crop":
      return (
        <InteractiveCropField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          allValues={allValues}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_crop_interactive":
      return (
        <InteractiveCropField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          allValues={allValues}
        />
      );

    case "special_image_or_pattern":
      return (
        <BooleanChoiceField
          {...commonProps}
          falseLabel="Pattern"
          trueLabel="Image"
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_manual_or_automatic":
      // ``manual_layout`` toggle. ``true`` = manual column grid,
      // ``false`` = automatic hero mode. Same two-option control family
      // as ``special_image_or_pattern`` (Background Pattern/Image). The
      // ``pendingRefresh`` matters here because flipping this field
      // changes ``show_if`` visibility on neighbouring fields
      // (``hero_layout`` ↔ ``hero_mode``/``hero_mode_animated``) AND
      // routes the *renderer* between ``HeroManual`` and
      // ``HeroAutomatic`` — the panel needs the freshest hero payload
      // so the metadata-driven layout mirrors the new mode.
      //
      // Manual → Automatic is a "start fresh" gesture, so the hero's
      // ``hero_mode`` is reset to the catalog default (``centered``).
      // Without this, whatever automatic mode the user had selected
      // *before* switching to Manual silently resurrects when they
      // come back — and because that mode is hidden from the panel
      // while in Manual, the user has no way to remember what it was.
      // The reset is emitted *first* so the autosave's deep-merged
      // payload sees both keys together; if it landed after, the
      // optimistic UI could briefly render the stale mode in between
      // the two PATCHes. The reset is *only* triggered on the actual
      // Manual → Automatic transition (gated on the previous value),
      // not on every click of the "Automatic" side, so re-clicking
      // the already-selected option leaves ``hero_mode`` alone.
      return (
        <BooleanChoiceField
          {...commonProps}
          falseLabel="Automatic"
          trueLabel="Manual"
          onChange={(val) => {
            const wasManual = value === true;
            const becomingAutomatic = val === false;
            if (wasManual && becomingAutomatic && selectedElement?.type === "hero") {
              onChange("hero_mode", "centered");
            } else if (wasManual && becomingAutomatic && selectedElement?.type === "component") {
              onChange("component_mode", componentModes?.default_mode || allValues?.component_mode || "centered");
            }
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_icon_picker":
      return (
        <IconPickerField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_video_url":
      return (
        <VideoUrlField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
        />
      );

    case "special_icon_position":
      return (
        <BooleanChoiceField
          {...commonProps}
          falseLabel="After Text"
          trueLabel="Before Text"
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_tight_or_loose":
      return (
        <BooleanChoiceField
          {...commonProps}
          falseLabel="Tight"
          trueLabel="Loose"
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          infoText={metadata.appearance_info_text}
        />
      );

    case "big_text":
      return (
        <TextField
          {...commonProps}
          multiline={true}
          infoText={metadata.appearance_info_text}
        />
      );

    case "small_text":
      return (
        <TextField
          {...commonProps}
          multiline={false}
          infoText={metadata.appearance_info_text}
        />
      );

    case "switch":
      // Render info icon next to label for switch fields instead of the generic wrapper.
      // ``sharesRow`` is forwarded from the grid so a switch packed onto a
      // multi-column line keeps its toggle next to the label (space trails after)
      // rather than flush-right against a narrow column edge.
      return (
        <SwitchField
          {...commonProps}
          infoText={metadata.appearance_info_text}
          sharesRow={sharesRow}
        />
      );

    case "checkbox":
      return (
        <CheckboxField
          {...commonProps}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_position":
      return (
        <PositionField
          {...commonProps}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_text_positions":
      return (
        <TextPositionField
          {...commonProps}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_text_hover_transform":
      return (
        <TextTransformAnimationField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_text_click_transform":
      return (
        <TextTransformAnimationField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            pendingRefreshRef.current = true;
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_spacing":
      return (
        <SpacingField
          {...commonProps}
          field={field}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_layout":
      return (
        <LayoutByLayoutField
          {...commonProps}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_hero_mode": {
      // The hero mode picker now owns BOTH the automatic-mode choice and
      // the manual/automatic switch (the old standalone "Layout Mode"
      // toggle is hidden — see ``Hero.manual_layout``). The picker shows
      // a "Manual" tile last in its gallery:
      //   * Clicking "Manual" flips ``manual_layout`` to True. We do NOT
      //     touch ``hero_mode`` — the dormant value is preserved so the
      //     picker keeps featuring the family the user had (the active
      //     panel + "All layouts" grid stay visible in Manual; only the
      //     slot manager is hidden). The backend no longer force-resets
      //     the mode on the way back to Automatic, so the preserved value
      //     survives a round-trip.
      //   * Clicking an automatic mode while Manual flips ``manual_layout``
      //     back to False and applies the picked mode in the same PATCH.
      // The slot manager only makes sense in automatic mode, so it is
      // suppressed while Manual.
      const heroManual = allValues?.manual_layout === true;
      return (
        <>
          <HeroModeField
            {...commonProps}
            manualLayout={heroManual}
            onSelectManual={() => {
              if (heroManual) return;
              onChange("manual_layout", true);
              pendingRefreshRef.current = true;
            }}
            onChange={(val) => {
              if (heroManual) {
                onChange("manual_layout", false);
              }
              onChange(field, val);
              pendingRefreshRef.current = true;
            }}
          />
          {!heroManual && <HeroSlotManagerField allValues={allValues} />}
        </>
      );
    }

    case "special_component_mode": {
      // Mirror of the hero picker: the "Manual" tile flips
      // ``manual_layout`` (Component has no Manual → Automatic reset gate,
      // so an explicit mode pick is always honoured and no mode reset is
      // needed on the way into Manual). The slot manager is hidden in
      // Manual mode.
      const componentManual = allValues?.manual_layout === true;
      return (
        <>
          <ComponentModeField
            {...commonProps}
            manualLayout={componentManual}
            onSelectManual={() => {
              if (componentManual) return;
              onChange("manual_layout", true);
              pendingRefreshRef.current = true;
            }}
            onChange={(val) => {
              if (componentManual) {
                onChange("manual_layout", false);
              }
              onChange(field, val);
              pendingRefreshRef.current = true;
            }}
            infoText={metadata.appearance_info_text}
          />
          {!componentManual && <ComponentSlotManagerField allValues={allValues} />}
        </>
      );
    }

    case "special_entrance_animation":
      return (
        <EntranceAnimationField
          {...commonProps}
          onChange={(val) => {
            onChange(field, val);
            // Entrance motion is rendered via a class on the hero root
            // and doesn't change layout metrics; the preview can update
            // after autosave confirms, no forced element refetch needed.
          }}
          choices={metadata.choices?.map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_hero_layout_builder":
      return (
        <HeroLayoutField
          {...commonProps}
        />
      );

    case "special_hero_height":
      return (
        <ByLayoutSliderField
          label={commonProps.label}
          help={metadata.appearance_info_text}
          value={value}
          onChange={(val) => onChange(field, val)}
          metadata={metadata}
          infoText={metadata.appearance_info_text}
          suggestions={[300, 500, 800, 1000, 1200, 1500]}
          min={50}
          max={2000}
        />
      );

    case "special_hero_column_widths":
      return (
        <HeroColumnWidthsField
          label={commonProps.label}
          help={metadata.appearance_info_text}
          value={value}
          onChange={(val) => onChange(field, val)}
          metadata={metadata}
          elementData={allValues}
        />
      );

    case "special_buttons_per_row":
      // Legacy; keep rendering if encountered
      return (
        <ButtonsPerRowField
          {...commonProps}
          onChange={(val) => onChange(field, val)}
          infoText={metadata.appearance_info_text}
        />
      );
    case "special_button_collection_alignment":
      return <ByLayoutChoiceField {...commonProps} onChange={(partial) => onChange(field, { ...(value || {}), ...partial })} choices={Object.values(metadata?.nested_fields || {})[0]?.choices?.map((c) => ({ value: c.value, label: c.label })) || []} infoText={metadata.appearance_info_text} />;
    case "special_font_size_by_layout": {
      return (
        <ByLayoutFontSizeField
          label={commonProps.label}
          help={metadata.appearance_info_text}
          value={value}
          onChange={(partial) => onChange(field, { ...(value || {}), ...partial })}
        />
      );
    }

    case "special_by_layout": {
      // Check if this is a layout field (vs padding, margin, etc.)
      if (metadata.appearance_title === "Layout" || metadata.name === "layout") {
        return (
          <LayoutByLayoutField
            key={metadata.name}
            value={value}
            onChange={(val) => onChange(field, val)}
            label={metadata.appearance_title || metadata.name}
            help={metadata.help_text}
          />
        );
      }
      
      // Children dispatch matrix. Each branch picks a dedicated by-layout
      // wrapper based on the *appearance* of the per-breakpoint child
      // models (extra_small / small / ... / extra_large). The match must
      // be on appearance, not just child count: every responsive wrapper
      // has exactly five children, so the legacy "Object.keys === 5"
      // shortcut would steer ResponsiveTextPosition / ResponsiveAlignment
      // into the spacing widget (top/right/bottom/left number boxes —
      // wrong shape, broken save). Each by-layout sibling
      // (``ByLayoutTextPositionField`` / ``ByLayoutChoiceField`` / etc.)
      // already encapsulates the screen-size mode selector + per-bp
      // editing UX, so the dispatch here only routes by content type.
      const nf = metadata?.nested_fields || {};
      const childMetas = Object.values(nf);
      const childTypes = childMetas.map((m) => (m?.appearance || m?.type || "").toString());
      const looksInteger = childTypes.every((t) => ["slider", "integer", "big_integer", "small_text", "special_font_size"].includes(t) || t.includes("integer"));
      const looksTextPosition = childMetas.length > 0 && childMetas.every(
        (m) => (m?.appearance || "").toString() === "special_text_positions"
      );
      const looksChoices = childMetas.length > 0 && childMetas.every(
        (m) => (m?.appearance || "").toString() === "choices"
      );
      // Spacing wrappers (``ResponsiveMargin`` / ``ResponsivePadding``)
      // declare each breakpoint as a OneToOne to a ``Margin`` /
      // ``Padding`` row whose own metadata exposes ``top`` / ``right``
      // / ``bottom`` / ``left`` children — i.e. each by-bp child shows
      // up here as ``appearance="nested"`` with ``nested_fields``
      // keyed on those four sides. Anchoring on that exact shape
      // distinguishes spacing wrappers from other five-child responsive
      // wrappers (text position, alignment, …) that share the
      // breakpoint count but ship totally different child shapes.
      const SPACING_SIDES = ["top", "right", "bottom", "left"];
      const looksSpacing = childMetas.length > 0 && childMetas.every((m) => {
        const appearance = (m?.appearance || "").toString();
        if (appearance !== "nested") return false;
        const grand = m?.nested_fields || {};
        return SPACING_SIDES.every((side) => Object.prototype.hasOwnProperty.call(grand, side));
      });
      if (looksInteger) {
        const nfTypes = Object.values(nf).map((m) => (m?.appearance || m?.type || "").toString());
        const anySlider = nfTypes.some((t) => t === "slider");
        if (anySlider) {
          return (
            <ByLayoutSliderField
              label={commonProps.label}
              help={metadata.appearance_info_text}
              value={value}
              onChange={(partial) => onChange(field, { ...(value || {}), ...partial })}
              metadata={metadata}
            />
          );
        }
        return (
          <ByLayoutIntegerField
            label={commonProps.label}
            help={metadata.appearance_info_text}
            value={value}
            onChange={(partial) => onChange(field, { ...(value || {}), ...partial })}
            metadata={metadata}
          />
        );
      }
      if (looksSpacing) {
        return (
          <ByLayoutSpacingField
            label={commonProps.label}
            help={metadata.appearance_info_text}
            value={value}
            onChange={(partial) => onChange(field, { ...(value || {}), ...partial })}
            field={field}
          />
        );
      }
      if (looksTextPosition) {
        return (
          <ByLayoutTextPositionField
            label={commonProps.label}
            help={metadata.appearance_info_text}
            value={value}
            onChange={(partial) => onChange(field, { ...(value || {}), ...partial })}
            infoText={metadata.appearance_info_text}
          />
        );
      }
      if (looksChoices) {
        // The CHOICES set is identical across every breakpoint child
        // (same Django ``choices=…`` arg on each ``AppearanceCharField``
        // in the wrapper model), so we can read it off the first child
        // safely. This keeps the dispatch agnostic to the specific
        // choice domain (alignment today, but anything else added in
        // the future inherits the same UX automatically).
        const firstChoices = (childMetas[0]?.choices) || [];
        return (
          <ByLayoutChoiceField
            label={commonProps.label}
            help={metadata.appearance_info_text}
            value={value}
            onChange={(partial) => onChange(field, { ...(value || {}), ...partial })}
            choices={firstChoices.map((c) => ({ value: c.value, label: c.label }))}
            infoText={metadata.appearance_info_text}
          />
        );
      }
      return (
        <NestedField
          field={field}
          value={value}
          metadata={metadata}
          onChange={onChange}
          parentFieldPath={parentFieldPath}
        />
      );
    }

    case "special_multiple_colors": {
      // Be flexible about where colors live (some UIs flatten Pattern.colors into a parent object)
      const extractColors = (obj, directValue) => {
        if (Array.isArray(directValue)) return directValue;
        if (!obj) return directValue;
        if (Array.isArray(obj.colors)) return obj.colors;
        if (obj.background_pattern && Array.isArray(obj.background_pattern.colors)) {
          return obj.background_pattern.colors;
        }
        if (obj.pattern && Array.isArray(obj.pattern.colors)) {
          return obj.pattern.colors;
        }
        return directValue;
      };

      const colorsValue = extractColors(allValues, value);
      return (
        <MultipleColorsField
          {...commonProps}
          value={colorsValue}
          infoText={metadata.appearance_info_text}
          paletteStorageScope={getPaletteStorageScope(field, normalizedMetadata)}
        />
      );
    }

    case "special_directions":
      return (
        <DirectionsField
          {...commonProps}
          infoText={metadata.appearance_info_text}
        />
      );

    case "special_pattern_visual": {
      // Be flexible about where colors live (pattern may be nested in different objects)
      const extractColors = (obj) => {
        if (!obj) return [];
        if (Array.isArray(obj.colors)) {
          return obj.colors;
        }
        if (obj.background_pattern && Array.isArray(obj.background_pattern.colors)) {
          return obj.background_pattern.colors;
        }
        if (obj.pattern && Array.isArray(obj.pattern.colors)) {
          return obj.pattern.colors;
        }
        return [];
      };
      const currentColors = extractColors(allValues);
      
      // Wrap onChange to log what PatternField is passing
      const handlePatternChange = (newPattern) => {
        // eslint-disable-next-line no-console
        console.log("[FOLDERS_PATTERN_FLOW] EditField.special_pattern_visual: PatternField called onChange", {
          field,
          newPattern,
          currentValue: value,
        });
        onChange(field, newPattern);
      };
      
      return (
        <PatternField
          {...commonProps}
          onChange={handlePatternChange}
          currentColors={currentColors}
          infoText={metadata.appearance_info_text}
        />
      );
    }

    case "special_websitepage_internal_link":
      return (
        <WebsitePageField
          label={commonProps.label}
          value={value}
          onChange={(val) => onChange(field, val)}
          websiteId={websiteId}
          infoText={metadata.appearance_info_text}
        />
      );


    case "image":
      return (
        <ImageField
          {...commonProps}
          onChange={(val) => onChange(field, val)}
          infoText={metadata.appearance_info_text}
        />
      );

    case "file":
      return (
        <FileField
          {...commonProps}
          onChange={(val) => onChange(field, val)}
          onOriginalFilenameChange={field === "file_url" ? (val) => onChange("original_filename", val) : undefined}
          onDisplayNameChange={field === "file_url" ? (val) => onChange("file_name", val) : undefined}
          onMultiFieldUpdate={field === "file_url" ? updateMultipleFields : undefined}
          infoText={metadata.appearance_info_text}
        />
      );

    case "nested":
      return (
        <div className={typography.bodySmall}>Special field: {field}</div>
      );

    case "float":
    case "integer":
    case "big_float":
    case "big_number":
      return (
        <input
          type="number"
          value={value || 0}
          onChange={(e) => onChange(field, parseFloat(e.target.value))}
          className={`w-full p-2 border rounded ${typography.body}`}
          step={metadata.appearance.includes("float") ? 0.1 : 1}
        />
      );

    case "hidden":
    case "readonly":
      return null;

    default:
      return <div className={typography.body}>Unknown field type: {metadata.appearance}</div>;
  }
};

export { EditField };
